import {activeCompanyId} from './portfolium-scope';

/**
 * Custodis is the one module that cannot run un-tenanted.
 *
 * Every other module treats `X-BC-Tenant` as a DATABASE SELECTOR and falls back to its control
 * database when the header is absent — see BC_Corporate's `AddDbContext` ("With no tenant ... we
 * fall back to the static control-database string") and BC_Identity's TenantContextMiddleware,
 * which simply calls next(). Isolation inside that control database is by company id.
 *
 * Custodis instead uses the tenant as a ROW DISCRIMINATOR inside one shared database — `TenantId`
 * is on VaultItem, PermissionGrant, AuditEvent, Customer, Organisation and StaffMember, and it is
 * the only isolation dimension those tables have (there is no company column). Its
 * TenantContextMiddleware therefore 401s an authenticated request that carries no tenant, rather
 * than falling through: "Token missing required identity claims."
 *
 * So on the general system — companies that never got a Billing tenant — the platform's
 * "fall back to a default" rule still applies, but the default has to preserve per-company
 * isolation or every company's secrets would land in one bucket. We derive it from the company the
 * tab has open:
 *
 *     company 26  ->  00000000-0000-0000-0000-000000000026
 *
 * Deterministic, readable in a row, parses as the GUID the middleware requires, and cannot collide
 * with a real (random) Billing tenant GUID.
 *
 * NEVER send this to another module. Identity and Corporate resolve a real connection string from
 * Billing for whatever GUID they are given and answer 502 "Unable to resolve tenant database" when
 * it is unknown — a synthetic id would take the whole app down, not just Custodis.
 *
 * Trade-off to remember: if one of these companies is later given a real Billing tenant, its vault
 * rows stay keyed to the synthetic GUID and need remapping (one UPDATE per TenantId table).
 *
 * Custodis reverses this mapping (CompanyVault in VaultReachService.cs) to find the vault of every
 * company the user belongs to, so a personal vault follows its owner between companies. Change the
 * format in both places or neither.
 */
export function custodisFallbackTenantId(): string | null {
  const companyId = activeCompanyId();
  if (companyId == null) return null;
  return `00000000-0000-0000-0000-${String(companyId).padStart(12, '0')}`;
}
